import { SandboxBooking, SandboxState, SandboxStore } from '../../store';
import { SandboxBookingInput } from '../../store/domains/bookings';
import { SandboxOperationHandler } from '../types';
import { hostView } from './hosts';
import { inputObject, page } from './utils';

const bookingView = (booking: SandboxBooking): Record<string, unknown> => ({
  ...booking,
  hosts: booking.hostIds,
});

const filteredBookings = (
  state: SandboxState,
  variables: Record<string, unknown>
): SandboxBooking[] => {
  const input = inputObject(variables);
  const hostIds =
    (variables.hostIds as number[] | undefined) ??
    (input.hostIds as number[] | undefined) ??
    [];
  const statuses =
    (variables.status as string[] | undefined) ??
    (input.status as string[] | undefined) ??
    [];
  const from = String(variables.from ?? input.from ?? '');
  const to = String(variables.to ?? input.to ?? '');
  return state.bookings.filter((booking) => {
    const matchesHosts =
      !hostIds.length || booking.hostIds.some((id) => hostIds.includes(id));
    const matchesStatus = !statuses.length || statuses.includes(booking.status);
    const matchesFrom = !from || Date.parse(booking.to) >= Date.parse(from);
    const matchesTo = !to || Date.parse(booking.from) <= Date.parse(to);
    return matchesHosts && matchesStatus && matchesFrom && matchesTo;
  });
};

const bookingInput = (
  variables: Record<string, unknown>
): SandboxBookingInput => {
  const input = inputObject(variables);
  return {
    from: String(input.from),
    to: String(input.to),
    hosts: (
      (input.hosts as number[] | undefined) ??
      (input.hostIds as number[] | undefined) ??
      []
    ).map(Number),
    client: (input.client as string | null | undefined) ?? null,
    comment: String(input.comment ?? ''),
    status: input.status as SandboxBooking['status'] | undefined,
  };
};

const overview = (store: SandboxStore): Record<string, unknown> => {
  const state = store.getState();
  const currentTimestamp = store.getCurrentTimestamp();
  return {
    hostGroupsOverview: state.hostGroups,
    hostsOverview: state.hosts.map((host) =>
      hostView(state, host.id, currentTimestamp)
    ),
  };
};

const listResult: SandboxOperationHandler = (variables, store) => ({
  getBookings: page(
    filteredBookings(store.getState(), variables).map(bookingView),
    variables
  ),
});

export const bookingResolvers: Record<string, SandboxOperationHandler> = {
  bookingFormData: (_, store) => overview(store),
  BookingsHostGroups: (_, store) => overview(store),
  bookingsV2: listResult,
  getBookingsV2: listResult,
  getBookingV2: (variables, store) => ({
    getBooking: bookingView(
      store
        .getState()
        .bookings.find((booking) => booking.id === Number(variables.id)) ??
        (() => {
          throw new Error('Бронь не найдена');
        })()
    ),
  }),
  createBookingV2: (variables, store) => ({
    createBooking: store.createBooking(bookingInput(variables)),
  }),
  createBooking: (variables, store) => ({
    createBooking: store.createBooking(bookingInput(variables)),
  }),
  updateBookingV2: (variables, store) => ({
    updateBooking: store.updateBooking(
      Number(variables.id),
      bookingInput(variables)
    ),
  }),
  updateBooking: (variables, store) => ({
    updateBooking: store.updateBooking(
      Number(variables.id),
      bookingInput(variables)
    ),
  }),
  redeemBooking: (variables, store) => {
    const input = inputObject(variables);
    return {
      setBookingStatus: store.redeemBooking(Number(variables.id ?? input.id)),
    };
  },
};
